import React, { useRef } from 'react';
import { AriaOverlayProps, useOverlay } from 'react-aria';
import { BackdropProps } from 'components/Backdrop';
import { OverlayContentsPosition } from 'components/Backdrop/types';
import DialogCloseButton from './DialogCloseButton';
import DialogContents, { DialogContentsProps } from './DialogContents';
import DialogTitle from './DialogTitle';
import * as S from './styles';

export interface DialogProps
  extends AriaOverlayProps,
    Omit<DialogContentsProps, 'overlayProps'>,
    Pick<BackdropProps, 'onExited'> {
  children?: React.ReactNode;
  className?: string;
  /**
   * The vertical position of the `Dialog`' contents (`children`) within the overlay
   */
  position?: OverlayContentsPosition;
  backdropClassName?: string;
}

interface DialogComponent extends React.FC<DialogProps> {
  CloseButton: typeof DialogCloseButton;
  Title: typeof DialogTitle;
}

/**
 * An accessible dialog component.
 *
 * A dialog is a type of modal window that appears in front of app content to provide
 * critical information or ask for a decision.  Dialogs disable all app functionality when
 * they appear and remain on screen until confirmed, dismissed, or a required action
 * has been taken.
 *
 * When the dialog is open, it prevents scrolling  on the underlying page and changes
 * focus to the first focusable element in the dialog.  When closed, scrolling is
 * restored and focus is restored to the last element that had focus when the dialog
 * opened.
 *
 * See <a href="https://react-spectrum.adobe.com/react-aria/useDialog.html" target="_blank">useDialog</a>
 * from react-aria.
 */
export const Dialog: DialogComponent = ({
  children,
  className,
  isDismissable = true,
  isOpen = false,
  onExited,
  position,
  backdropClassName,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const { overlayProps, underlayProps } = useOverlay(
    {
      ...props,
      isDismissable,
      isOpen,
    },
    ref,
  );

  return (
    // Backdrop will mount/unmount its child based on the value of isOpen.  Since
    // react-aria expects its dialog hooks to run only when the dialog is mounted,
    // a separate DialogContents component exists which includes those hooks.
    <S.Backdrop
      contentsPosition={position}
      overlay
      isOpen={isOpen}
      onExited={onExited}
      className={backdropClassName}
      {...underlayProps}
    >
      <DialogContents {...{ className, overlayProps, ref }} {...props}>
        {children}
      </DialogContents>
    </S.Backdrop>
  );
};

Dialog.CloseButton = DialogCloseButton;
Dialog.Title = DialogTitle;

export default Dialog;
